import React, { useState, useEffect } from "react";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { store } from "./store";
// AlterCPA removed for Serbia-CC
// import { useAppDispatch } from "./store/hooks";
// import { fetchAlterCPAData } from "./store/slices/altercpaSlice";
import Login from "./components/Login";
import AuthenticatedApp from "./components/AuthenticatedApp";
import LoadingSpinner from "./components/LoadingSpinner";
import { API_BASE_URL } from "./utils/apiConfig";
import { LanguageProvider } from "./contexts/LanguageContext";

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  countries: string[];
  permissions: string[];
  profile: {
    firstName: string;
    lastName: string;
    phone: string;
    department: string;
  };
}

const AppContent: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // AlterCPA removed for Serbia-CC
  // const dispatch = useAppDispatch();

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem("authToken");
    const savedUser = localStorage.getItem("user");
    const sessionExpiration = localStorage.getItem("sessionExpiration");
    const rememberMe = localStorage.getItem("rememberMe");

    console.log("🔍 [AUTH] Checking stored authentication data...");
    console.log("🔍 [AUTH] Token exists:", !!token);
    console.log("🔍 [AUTH] User exists:", !!savedUser);
    console.log("🔍 [AUTH] Session expiration:", sessionExpiration);
    console.log("🔍 [AUTH] Remember me:", rememberMe);

    if (token && savedUser) {
      try {
        // Check if session has expired
        if (sessionExpiration) {
          const expirationTime = parseInt(sessionExpiration);
          const currentTime = Date.now();
          const timeLeft = expirationTime - currentTime;

          console.log(
            "🕒 [AUTH] Current time:",
            new Date(currentTime).toLocaleString()
          );
          console.log(
            "🕒 [AUTH] Expiration time:",
            new Date(expirationTime).toLocaleString()
          );
          console.log(
            "🕒 [AUTH] Time left:",
            Math.round(timeLeft / (1000 * 60 * 60)),
            "hours"
          );

          if (currentTime > expirationTime) {
            console.log("❌ [AUTH] Session expired, clearing stored data");
            handleLogout();
            setLoading(false);
            return;
          }
        } else {
          console.log(
            "⚠️ [AUTH] No session expiration found, setting default 24h"
          );
          // If no expiration is set, set a default 24-hour expiration
          const defaultExpiration = Date.now() + 24 * 60 * 60 * 1000;
          localStorage.setItem(
            "sessionExpiration",
            defaultExpiration.toString()
          );
        }

        const userData = JSON.parse(savedUser);
        setUser(userData);
        setIsAuthenticated(true);

        console.log("✅ [AUTH] Auto-login successful", {
          user: userData.email,
          remembered: !!rememberMe,
          role: userData.role,
        });

        // Verify token is still valid (don't await this to avoid blocking UI)
        verifyToken(token).catch((err) => {
          console.error(
            "❌ [AUTH] Token verification failed during auto-login:",
            err
          );
        });
      } catch (err) {
        // Invalid saved data, clear it
        console.error("❌ [AUTH] Invalid saved data, clearing storage:", err);
        handleLogout();
      }
    } else {
      console.log("ℹ️ [AUTH] No stored authentication data found");
    }

    setLoading(false);
  }, []);

  const verifyToken = async (token: string) => {
    try {
      console.log("🔐 [AUTH] Verifying token validity...");
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: "include", // Important for cross-origin cookies
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.log(
          "❌ [AUTH] Token verification failed - HTTP",
          response.status
        );
        throw new Error(`Token invalid - HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        console.log("✅ [AUTH] Token is valid, updating user data");
        setUser(data.data.user);
        localStorage.setItem("user", JSON.stringify(data.data.user));

        // AlterCPA removed for Serbia-CC
        // if (data.data.altercpa) {
        //   console.log("🔄 [AUTH] Loading AlterCPA data into Redux store...");
        //   dispatch(fetchAlterCPAData.fulfilled(data.data.altercpa, ""));
        // }
      } else {
        console.log(
          "❌ [AUTH] Token verification failed - API response",
          data.error
        );
        throw new Error("Token verification failed");
      }
    } catch (err) {
      console.error("❌ [AUTH] Token verification error:", err);
      // Only logout if we're currently authenticated and the error is auth-related
      // Don't logout on network errors during background verification
      if (
        err instanceof Error &&
        (err.message.includes("Token invalid") ||
          err.message.includes("Token verification failed") ||
          err.message.includes("HTTP 401") ||
          err.message.includes("HTTP 403"))
      ) {
        console.log("🚪 [AUTH] Invalid token detected, logging out");
        handleLogout();
      } else {
        console.log(
          "⚠️ [AUTH] Network/temporary error during token verification, keeping session"
        );
      }
    }
  };

  const handleLogin = (token: string, userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = React.useCallback(() => {
    console.log("🚪 [AUTH] Logging out user");
    setUser(null);
    setIsAuthenticated(false);

    const itemsToRemove = [
      "authToken",
      "user",
      "refreshToken",
      "rememberMe",
      "rememberedEmail",
      "sessionExpiration",
      "loginTime",
    ];
    itemsToRemove.forEach((item) => {
      try {
        if (localStorage.getItem(item)) {
          localStorage.removeItem(item);
        }
      } catch {
        // ignore
      }
    });
    console.log("✅ [AUTH] Logout completed, localStorage cleared");
  }, []);

  useEffect(() => {
    const onAuthLogout = () => handleLogout();
    window.addEventListener("auth:logout", onAuthLogout);
    return () => window.removeEventListener("auth:logout", onAuthLogout);
  }, [handleLogout]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Login onLogin={handleLogin} loading={loading} />;
  }

  return <AuthenticatedApp user={user} onLogout={handleLogout} />;
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </BrowserRouter>
    </Provider>
  );
};

export default App;
